import { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Grid, Paper } from '@mui/material';

import {
  createLicense,
  createDemoLicense,
  checkLicenseModules,
} from '../../api/analyzerLicenses';
import { useIsSuperUser } from '../auth/hooks/useIsSuperUser';

import CreateLicensesForm from './CreateLicensesForm';
import LicenseList from './LicenseList';
import LicenseOutputDialog from './LicenseOutputDialog';
import { LicenseForm } from './types';

const initialLicenseState: LicenseForm = {
  serial: 'New License',
  modules: [],
};

const CreateLicensesPage = () => {
  const navigate = useNavigate();

  const [licenses, setLicenses] = useState<LicenseForm[]>([]);
  const [selectedLicenseIndex, setSelectedLicenseIndex] = useState(-1);
  const [createdLicense, setCreatedLicense] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isDemoLicense, setIsDemoLicense] = useState(false);
  const isSuperUser = useIsSuperUser();

  const handleUpdateLicense = (updatedLicense: LicenseForm) => {
    if (selectedLicenseIndex > -1) {
      const licensesArray = [...licenses];
      licensesArray[selectedLicenseIndex] = updatedLicense;
      setLicenses(licensesArray);
    }
  };

  const handleCreateNewLicense = () => {
    setLicenses([...licenses, initialLicenseState]);
    setSelectedLicenseIndex(licenses.length + 1);
  };

  const handleDeleteLicense = (index: number) => {
    const licenseArray = [...licenses];
    licenseArray.splice(index, 1);
    setLicenses(licenseArray);
    setSelectedLicenseIndex(-1);
  };

  const handleDemoLicenseChecked = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    setIsDemoLicense(checked);
  };

  const handleCreateLicense = async () => {
    let createdLicense;
    const date = new Date();
    if (isDemoLicense) {
      const demoLicenseArray = licenses.map((license) => ({
        ...license,
        demo_start_date: date.toISOString().split('T')[0],
      }));
      createdLicense = await createDemoLicense(demoLicenseArray);
    } else {
      createdLicense = await createLicense(licenses);
    }
  
    const jsonString = JSON.stringify(createdLicense);
  
    const blob = new Blob([jsonString], { type: 'application/json' });
  
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'createdLicense.lic'; 
    document.body.appendChild(a); 
    
    a.click();
  
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };
  

  const handleImportLicense = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result;
      if (!text || typeof text !== 'string') return;
      const licenseJson = JSON.parse(text);
    
      const parsedLicenses = licenseJson.map((license: { license: string; }) => {
        const demoStartDate = extractStartDateFromLicense(license.license);
        return {
          ...license,
          demoStartDate: demoStartDate || null, 
        };
      });
    
      setLicenses([...licenses, ...parsedLicenses]);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  function extractStartDateFromLicense(licenseString: string) {
    const potentialEncodedDate = licenseString.slice(-16);
    try {
      const decodedDate = atob(potentialEncodedDate);
      if (/\d{4}-\d{2}-\d{2}/.test(decodedDate)) {
        return decodedDate;
      }
    } catch (error) {
      console.error("Error decoding date:", error);
    }
  
    return null;
  }
  
  const handleLicenseClicked = (index: number) => {
    setIsDemoLicense(false);
    setSelectedLicenseIndex(index);
  };

  if (isSuperUser && process.env.REACT_APP_MODE !== 'LPT') {
    navigate('/');
    return <></>;
  }

  return (
    <>
      <LicenseOutputDialog
        open={dialogOpen}
        createdLicense={createdLicense}
        handleClose={() => setDialogOpen(false)}
      />
      <Grid container>
        <Grid item xs={6} component={Paper}>
          <LicenseList
            licenses={licenses}
            handleDeleteLicense={handleDeleteLicense}
            handleLicenseClicked={handleLicenseClicked}
            selectedLicenseIndex={selectedLicenseIndex}
          />
        </Grid>
        <Grid item xs={6} component={Paper}>
          <CreateLicensesForm
            handleUpdateLicense={handleUpdateLicense}
            handleCreateNewLicense={handleCreateNewLicense}
            handleCreateLicense={handleCreateLicense}
            handleImportLicense={handleImportLicense}
            licenseAmount={licenses.length}
            licenses={licenses}
            selectedLicenseIndex={selectedLicenseIndex}
            isDemoLicense={isDemoLicense}
            handleDemoLicenseChecked={handleDemoLicenseChecked}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default CreateLicensesPage;
